import isError from 'lodash/isError';
import isObject from 'lodash/isObject';
import isString from 'lodash/isString';
import get from 'lodash/get';
import orderBy from 'lodash/orderBy';
import { OrderBy } from './appConstant';
import {
  MEMO_PRICE_TYPES,
  MEMO_TYPES,
  BOOK_CREW_STATUS_TYPES
} from '@iso/enums';

export function showServerError(error) {
  if (!error) return;

  if (isError(error)) {
    return error.message;
  }

  if (isObject(error)) {
    const errorTypes = Object.keys(error);

    if (errorTypes.length === 0) return null;

    if (errorTypes.length > 0) {
      const firstError = error[errorTypes[0]];

      if (isString(firstError)) {
        return firstError;
      } else if (Array.isArray(firstError)) {
        return firstError.join(',');
      } else {
        return 'Unknown error happened';
      }
    }
  }

  return error;
}

export function stringToInt(value, defValue = 0) {
  if (!value) {
    return 0;
  } else if (!isNaN(value)) {
    return parseInt(value, 10);
  }
  return defValue;
}
export function stringToPositiveInt(value, defValue = 0) {
  const val = stringToInt(value, defValue);
  return val > -1 ? val : defValue;
}

export const formatNumber = (number = 0) => {
  return number.toString().replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,');
};

export const formatPhoneNumber = (number) => {
  //Filter only numbers from the input
  const cleaned = `${number}`.replace(/\D/g, '');
  //Check if the input is of correct length
  let match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);

  if (match) {
    return `${match[1]}-${match[2]}-${match[3]}`;
  }

  return number;
};

export const formatCurrency = (unit, number = 0) => {
  let num = number;
  if (typeof num !== 'number') {
    num = isNaN(parseFloat(num)) ? 0 : parseFloat(num);
  }
  return `${unit}${formatNumber(num.toFixed(2))}`;
};

export const reorder = (list, startIndex, endIndex) => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);
  return result;
};

export const isValidEmail = (email) =>
  /[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?/.test(
    email
  );

export const getMutualConnections = (connections) => {
  let mutualConnectionText;
  if (connections.length) {
    mutualConnectionText = connections[0].fullName;
    if (connections.length > 1) {
      mutualConnectionText += ` and ${connections.length - 1} other${
        connections.length === 2 ? '' : 's'
      }`;
    }
  }
  return mutualConnectionText;
};

export const sortData = (arr, field, order = OrderBy.ASC) => {
  const sortByData = orderBy(
    arr,
    [(user) => (user[field] || '').toLowerCase()],
    [order]
  );
  return sortByData;
};

export const sortContact = (arr, field, order = OrderBy.ASC) => {
  const sortedData = orderBy(
    arr,
    [
      (user) =>
        user[field]
          ? (user[field] || '').toLowerCase()
          : (user['email'] || '').toLowerCase()
    ],
    [order]
  );
  return sortedData;
};

export const getMemoAmount = (memoDetail) => {
  let calcAmt = 0;
  const { workingDays, workingRate, kitFee, projectRate, priceType } =
    memoDetail;
  if (priceType === MEMO_PRICE_TYPES.HOURLY) {
    calcAmt = eval(workingDays * workingRate) + kitFee;
  } else {
    calcAmt = eval(projectRate + kitFee);
  }
  return calcAmt;
};

export const getMemoExtraRateFieldsAmount = (rates) => {
  let rateAmount = 0;
  rates.forEach((rate) => {
    const { dayRate, priceType, projectRate, numberOfDays } = rate;
    if (priceType === MEMO_PRICE_TYPES.FIXED && projectRate) {
      rateAmount += isNaN(projectRate) ? 0 : projectRate;
    } else if (
      priceType === MEMO_PRICE_TYPES.HOURLY &&
      dayRate &&
      numberOfDays
    ) {
      rateAmount += isNaN(dayRate * numberOfDays) ? 0 : dayRate * numberOfDays;
    }
  });
  return rateAmount;
};

export const getMemoPriceWithAddRateExtraField = (memoDetail) => {
  let totalMemoPrice = 0;
  const { rates } = memoDetail;
  const memoAmount = getMemoAmount(memoDetail);
  const memoExtraRateAmount = getMemoExtraRateFieldsAmount(rates || []);
  totalMemoPrice = memoAmount + memoExtraRateAmount
  totalMemoPrice = totalMemoPrice.toFixed(2)

  return totalMemoPrice;
};

export const sortList = (optionA, optionB, sortOption) => {
  let valueA = get(optionA, sortOption.sortKey);
  let valueB = get(optionB, sortOption.sortKey);
  if (valueA && typeof valueA === 'string') {
    valueA = valueA.toUpperCase();
  }
  if (valueB && typeof valueB === 'string') {
    valueB = valueB.toUpperCase();
  }
  let sortVal = 0;
  if (valueA > valueB) {
    sortVal = 1;
  }
  if (valueA < valueB) {
    sortVal = -1;
  }
  if (sortVal !== 0 && sortOption.sortDir === OrderBy.DESC) {
    return sortVal * -1;
  }
  return sortVal;
};

export const tagFormattedEmails = (emails) => {
  if (!emails) return [];
  return emails.map((email) => ({ id: email, name: email }));
};

export const downloadFile = (data, fileName) => {
  const downloadUrl = window.URL.createObjectURL(new Blob([data]));
  const link = document.createElement('a');
  link.href = downloadUrl;
  link.setAttribute('download', fileName);
  document.body.appendChild(link);
  link.click();
  link.remove();
};

export const replaceBlankSpace = (value, replaceText = '_') => {
  return value.replace(/ /g, replaceText);
};

export const getW9AttachmentName = (authUser, file) => {
  return replaceBlankSpace(
    `${authUser.fullName}_W9.${file.name.split('.').pop()}`
  );
};

export const formatRatesFields = (rates) => {
  rates.forEach((e) => {
    delete e.isEdit;
    if (e.priceType === MEMO_PRICE_TYPES.FIXED) {
      delete e.numberOfDays;
      delete e.dayRate;
    } else if (e.priceType === MEMO_PRICE_TYPES.HOURLY) {
      delete e.projectRate;
    }
  });
};

export const getBookCrewStatus = ({ crew }) => {
  if (crew.memoType === MEMO_TYPES.HOLD) {
    return '';
  } else {
    if (crew.accepted) {
      return BOOK_CREW_STATUS_TYPES.CONFIRMED;
    } else if (crew.decline) {
      return BOOK_CREW_STATUS_TYPES.DECLINED;
    } else if (crew.booked) {
      return BOOK_CREW_STATUS_TYPES.MEMO_SENT;
    } else {
      return BOOK_CREW_STATUS_TYPES.MEMO_SAVED;
    }
  }
};

export const getIsShouldBookCrewConsidered = (crew, crewIndex, crewList) => {
  if (crew?.memoStatus !== BOOK_CREW_STATUS_TYPES.MEMO_SAVED) {
    return false;
  }
  if (crewIndex !== 0) {
    const filteredCrewList = crewList.filter((_, i) => i < crewIndex);
    for (let i = 0; i < filteredCrewList.length; i++) {
      if (
        !filteredCrewList[i].memoStatus ||
        filteredCrewList[i].memoStatus !== BOOK_CREW_STATUS_TYPES.DECLINED
      ) {
        return false;
      }
    }
    return true;
  }
  return true;
};

export const generateInvitationList = (emails, query) => {
  const emailArr = [];
  emails.forEach((email) => {
    emailArr.push({
      full_name: email,
      email
    });
  });

  if (query && isValidEmail(query)) {
    // Check duplicated email
    const invitationArray = emails.map((name) => name.toLowerCase());
    if (!invitationArray.includes(query.toLowerCase())) {
      emailArr.push({
        full_name: query,
        email: query
      });
    }
  }
  return emailArr;
};

export const generateWindowDimensions = () => {
  const { screen } = window;
  const width = screen.width > 797 ? 600 : screen.width - 30;
  const height = screen.width > 797 ? 600 : screen.width - 30;
  const left = screen.width / 2 - width / 2;
  const top = screen.height / 2 - height / 2;
  return { width, height, left, top };
};
export const truncateWithEllipses = (input, max = 10) =>
  input.length > max ? `${input.substring(0, max - 1)}...` : input;

export const getUserFullName = (user) => {
  if (!user || !user?.fullName) return { firstName: '', lastName: '' };
  const { firstName, lastName } = user;
  const [firstNameOld, lastNameOld] = user.fullName.split(' ');
  return {
    firstName: firstName || firstNameOld,
    lastName: lastName || lastNameOld
  };
};